import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { object, string } from 'yup';
import { FormikProps } from 'formik';
import isEmpty from 'lodash/isEmpty';

// actions
import {
  createIvsChannel,
  IvsChannel,
  IvsChannelType,
  IvsChannelVideoLatencyOption,
  NewIvsChannelParams,
} from 'ducks/merchantDucks/items/actions';

// selectors
import { getItemDetails, getIvsChannelsData } from 'ducks/merchantDucks/items/selectors';

// hooks
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';
import { useUncontrolledSwitch } from 'components/Switcher';

// components
import Loader from 'components/Loader';
import FormikWrapper from 'components/FormikWrapper';

// own types
import { IvsContentObject } from '../IvsContent';

// own components
import ChannelOptions from './ChannelOptions';

const validationSchema = object().shape({
  name: string()
    .matches(/^\S{1,}$/g, 'No empty spaces or special characters allowed')
    .required('Channel name is required'),
});

const initialValues: NewIvsChannelParams = {
  name: '',
  type: IvsChannelType.STANDARD,
  latency_mode: IvsChannelVideoLatencyOption.NORMAL,
  authorized: true,
};

interface Props {
  closePanel: () => void;
  onSubmit: (channelContent: IvsContentObject) => void;
}

const NewChannel: FC<Props> = ({ closePanel, onSubmit }) => {
  const dispatch = useDispatch();

  const { resetToDefault } = useUncontrolledSwitch();

  const { isFetching: isItemDetailsFetching } = useSelector(getItemDetails);

  const {
    status: { isFetching: isIvsChannelsFetching },
  } = useSelector(getIvsChannelsData);

  const [, setSaveButtonDisabled] = useSharedBoolean('isSaveButtonDisabled', false);

  const handleSubmit = async (values: NewIvsChannelParams) => {
    const data = await Promise.resolve(dispatch(createIvsChannel(values)));

    if (data) {
      const { arn, stream_url } = data as IvsChannel;

      onSubmit({ arn, stream_url });
    }
  };

  const handleStatusChange = ({ isSubmitting, errors, isValid }: FormikProps<NewIvsChannelParams>) => {
    setSaveButtonDisabled(isSubmitting || !isValid || !isEmpty(errors));
  };

  if (isItemDetailsFetching || isIvsChannelsFetching) {
    return <Loader position="absolute" />;
  }

  return (
    <FormikWrapper<NewIvsChannelParams>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      closePanel={closePanel}
      validationSchema={validationSchema}
      onStatusChange={handleStatusChange}
      validateOnMount
    >
      <ChannelOptions title="Create a new IVS channel" onCancel={resetToDefault} />
    </FormikWrapper>
  );
};

export default NewChannel;
